import { isAfter, startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { getPreapprovedSuffix } from "../../components/ServiceCreation/ServiceBuilder/ServiceBuilder.utils";
import { serviceBillingModel, serviceCostModel, serviceModel } from "../../models";
import { fromDateStampObject } from "../../models/time";
import { formatShortMonthUSDate } from "./..";
import { utils } from '..';
import pluralize from "../pluralize";
export const ServiceDescriptionBadgesTestIds = pathgen('ServiceDescriptionBadgesTestIds');
export function isFutureEffectiveDate() {
  let effectiveDate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  let now = arguments.length > 1 ? arguments[1] : undefined;
  return !!(effectiveDate && now && isAfter(startOfDay(fromDateStampObject(effectiveDate)), startOfDay(now)));
}
function optionalBadge(service) {
  return serviceModel.isOptional(service.participation) ? {
    skin: 'warning',
    intent: 'optional',
    text: 'Optional',
    tooltip: 'This service is marked as optional'
  } : null;
}
function pausedBadge(service) {
  return serviceBillingModel.isPaused(service.billing) ? {
    skin: 'warning',
    intent: 'paused',
    text: 'Paused',
    tooltip: 'Automatic billing is paused for this service'
  } : null;
}
function proratedBadge(service) {
  return serviceBillingModel.isProrated(service.billing) ? {
    skin: 'warning',
    intent: 'prorated',
    text: 'Prorated',
    tooltip: 'This service is prorated'
  } : null;
}
function preapprovedBadge(service, PREAPPROVED_AMOUNT_IMPROVMENTS) {
  const {
    cost
  } = service;
  const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing) || 'onetime';
  const suffix = getPreapprovedSuffix(recurrencePeriod);
  return serviceCostModel.isVariablePrice(cost) && cost.unitCap && cost.unitCap.gt(0) ? {
    skin: 'success',
    intent: 'preapproved',
    text: "".concat(utils.formatNumberWithCommas(cost.unitCap), " ").concat(pluralize(serviceCostModel.formattedUnitName(cost), cost.unitCap.toNumber()), " preapproved").concat(suffix && PREAPPROVED_AMOUNT_IMPROVMENTS ? " per ".concat(suffix.substring(1)) : '')
  } : null;
}
export function discountBadge(service) {
  const activeDiscount = serviceModel.discount(service);
  const expiredDiscount = serviceModel.expiredDiscount(service);
  const discount = activeDiscount || expiredDiscount;
  if (!discount || !discount.amount().gt(0)) {
    return null;
  }
  const expiration = discount.expiration();
  const isActiveDiscount = !!activeDiscount && (expiration === null || expiration === void 0 ? void 0 : expiration.isActive());
  const isExpiredDiscount = !!expiredDiscount;
  if (isActiveDiscount && expiration) {
    const expirationDate = expiration.date();
    const expirationPeriod = expiration.period();
    const isExpirationPeriodType = expiration.isExpirationPeriodType();
    if (expirationDate) {
      return {
        skin: 'success',
        intent: "limited-".concat(discount.type(), "-discount"),
        text: "".concat(discount.format(), " off Until ").concat(formatShortMonthUSDate(expirationDate))
      };
    }
    if (isExpirationPeriodType && expirationPeriod) {
      return {
        skin: 'success',
        intent: "limited-".concat(discount.type(), "-discount"),
        text: "".concat(discount.format(), " off for ").concat(pluralize(expirationPeriod, expiration.amount(), true))
      };
    }
  }
  if (isExpiredDiscount && expiration) {
    return {
      skin: 'success',
      intent: "expired-".concat(discount.type(), "-discount"),
      text: "".concat(discount.format(), " off (expired)")
    };
  }
  return {
    skin: 'success',
    intent: "".concat(discount.type(), "-discount"),
    text: "".concat(discount.format(), " Off")
  };
}
function limitedTimesBadge(service) {
  return serviceModel.maxChargesEnabled(service) ? {
    skin: 'warning',
    intent: 'limit',
    text: "Can be billed ".concat(serviceBillingModel.formatMaxChargesLimit(service.billing)),
    tooltip: 'This service has a limited number of charges'
  } : null;
}
function priceIncreaseBadge(service) {
  return serviceModel.priceIncrease(service).exists() ? {
    skin: 'warning',
    intent: 'price-increase',
    text: 'Auto-price increase'
  } : null;
}
export function billingDetailsBadge(service, options, featureFlags) {
  const {
    billing
  } = service;
  const {
    effectiveDate = null,
    now,
    isClientPreview,
    isActiveAgreement
  } = options;
  const {
    BILLED_EFFECTIVE_DATE_TEXT
  } = featureFlags;
  const formatTextForPreview = (text, suffix) => "".concat(text).concat(isClientPreview ? '' : suffix);
  const formatRecurringTextForPreview = (text, suffix) => {
    if (isClientPreview) {
      const maxCharges = serviceModel.maxChargesEnabled(service) ? serviceModel.maxChargesLimit(service) : null;
      const maxChargesText = maxCharges ? ", ".concat(maxCharges, " times") : '';
      return "Billed ".concat(text).concat(maxChargesText);
    }
    return "Billed ".concat(text).concat(suffix);
  };
  return serviceBillingModel.accept(billing, {
    visitOnApproval: () => {
      if (BILLED_EFFECTIVE_DATE_TEXT) {
        if (isFutureEffectiveDate(effectiveDate, now)) {
          return {
            text: 'Billed once, on effective date',
            intent: 'type-effective-date'
          };
        } else if (isActiveAgreement) {
          return {
            text: 'Billed once, immediately',
            intent: 'type-approval-amendment'
          };
        }
        return {
          text: 'Billed once, on proposal acceptance',
          intent: 'type-approval'
        };
      }
      return isActiveAgreement ? {
        text: 'Billed once, automatically',
        intent: 'type-approval-amendment'
      } : {
        text: 'Billed once, on proposal acceptance',
        intent: 'type-approval'
      };
    },
    visitOneTimeManual: () => ({
      text: formatTextForPreview('Billed once', ', manually'),
      intent: 'type-onetime-manual'
    }),
    visitRepeatableManual: billing => ({
      text: formatRecurringTextForPreview(billing.recurrencePeriod, ', manually'),
      intent: 'type-repeatable-manual'
    }),
    visitOneTimeInStages: billing => ({
      text: "Billed in ".concat(pluralize('stage', billing.oneTimeInStages.numStages(), true)),
      intent: 'type-one-time-in-stages'
    }),
    visitOngoing: billing => ({
      text: formatRecurringTextForPreview(billing.recurrencePeriod, ', automatically'),
      intent: 'type-ongoing'
    })
  });
}
function initialBillingBadge(service, agreementData, now, isFreshlyAdded, featureFlags) {
  const {
    billing
  } = service;
  let isBillOnPeriodBeforeNextBilling = false;
  if (featureFlags.START_BILLING_AT) {
    isBillOnPeriodBeforeNextBilling = serviceModel.isBillOnPeriodBeforeNextBilling(service);
  } else {
    isBillOnPeriodBeforeNextBilling = serviceBillingModel.isOngoing(billing) && serviceBillingModel.isBilledUpfront(billing) && !serviceBillingModel.isSkipBillingOnAcceptance(billing);
  }
  if (isBillOnPeriodBeforeNextBilling) {
    const effectiveDate = agreementData.effectiveDate;
    const isActiveAgreement = agreementData.isActiveAgreement();
    if (featureFlags.START_BILLING_AT && serviceModel.startBillingAt(service)) {
      return null;
    }
    if (isActiveAgreement) {
      if (isFreshlyAdded) {
        return {
          skin: 'warning',
          intent: 'bill-immediately',
          text: 'Charged immediately',
          tooltip: 'This service will be billed immediately'
        };
      }
      return null;
    } else {
      if (isFutureEffectiveDate(effectiveDate, now)) {
        return {
          skin: 'warning',
          intent: 'bill-on-effective-date',
          text: 'Charged on effective date',
          tooltip: 'This service will be billed on the effective date of the agreement'
        };
      }
      return {
        skin: 'warning',
        intent: 'bill-on-approval',
        text: 'Charged on acceptance',
        tooltip: 'This service will be billed on agreement approval'
      };
    }
  } else {
    return null;
  }
}
function conditionalDiscountBadge(service) {
  const expiration = serviceModel.limitedTimeDiscountOfferExpiration(service);
  if (!expiration) {
    return null;
  }
  return {
    skin: 'success',
    intent: 'conditional-discount',
    text: 'Conditional Discount'
  };
}
function startBillingAtBadge(service, _ref) {
  let {
    context,
    now,
    isFreshlyAdded
  } = _ref;
  const startBillingAt = serviceModel.startBillingAt(service);
  const isAgreement = context === 'agreement';
  if (!startBillingAt) {
    return null;
  }
  if (isAgreement && !isFreshlyAdded && !isAfter(startOfDay(startBillingAt), startOfDay(now))) {
    return null;
  }
  return {
    skin: 'warning',
    intent: 'start-billing-at',
    badgeTestid: ServiceDescriptionBadgesTestIds.StartBillingAt(),
    text: "Billing starts on ".concat(formatShortMonthUSDate(startBillingAt))
  };
}
function bundleBadge(service, SERVICE_TEMPLATE_BUNDLE) {
  return serviceModel.isBundle(service) && SERVICE_TEMPLATE_BUNDLE ? {
    intent: 'bundle',
    text: 'Bundle'
  } : null;
}
export function getServiceDescriptionBadges(service, agreementData, now) {
  let featureFlags = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  let {
    hideBillingDetailsBadge = false,
    hideDiscountBadge = false,
    hidePreapprovedBadge = false,
    hideProratedBadge = false,
    hidePausedBadge = false,
    hideOptionalBadge = false,
    hideLimitedTimesBadge = false,
    hideInitialBillingBadge = false,
    hidePriceIncreaseBadge = false,
    hideConditionalDiscountBadge = false,
    hideBundleBadge = false,
    isFreshlyAdded = false,
    context = 'draft'
  } = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  return compact([!hideBundleBadge && bundleBadge(service, featureFlags.SERVICE_TEMPLATE_BUNDLE || false), !hideBillingDetailsBadge && billingDetailsBadge(service, {
    isActiveAgreement: agreementData.isActiveAgreement(),
    effectiveDate: agreementData.effectiveDate,
    now
  }, featureFlags), !hideDiscountBadge && discountBadge(service), !hidePreapprovedBadge && preapprovedBadge(service, featureFlags.PREAPPROVED_AMOUNT_IMPROVMENTS || false), !hideProratedBadge && proratedBadge(service), !hidePausedBadge && pausedBadge(service), !hideOptionalBadge && optionalBadge(service), !hideLimitedTimesBadge && limitedTimesBadge(service), !hideInitialBillingBadge && initialBillingBadge(service, agreementData, now, isFreshlyAdded, featureFlags), !hidePriceIncreaseBadge && priceIncreaseBadge(service), !hideConditionalDiscountBadge && conditionalDiscountBadge(service), featureFlags.START_BILLING_AT && startBillingAtBadge(service, {
    now,
    isFreshlyAdded,
    context
  })]);
}